/**
 * AGENT EARNINGS (2026-11-01).
 *
 * Commission is a percentage of what was actually COLLECTED, net of the Stripe fee — so every row shows the
 * gross, the card fee and the base the percentage was applied to. Showing only the commission would leave an
 * agent unable to check the arithmetic.
 */
import React, { useState } from "react";
import {
  AgentPageState,
  Badge,
  formatDate,
  formatMonth,
  money,
  payoutTone,
  styles,
  useAgentResource,
} from "./agentShared";

function Stat({ label, value, hint }) {
  return (
    <div style={styles.stat}>
      <span style={styles.statLabel}>{label}</span>
      <div style={styles.statValue}>{value}</div>
      {hint ? <span style={styles.muted}>{hint}</span> : null}
    </div>
  );
}

export default function AgentEarnings() {
  const { loading, error, unavailable, data, reload } = useAgentResource(
    "/api/platform-plans/referral/earnings",
  );
  const [openMonth, setOpenMonth] = useState(null);

  const commissions = Array.isArray(data?.commissions) ? data.commissions : [];
  const months = Array.isArray(data?.months) ? data.months : [];
  const visible = openMonth
    ? commissions.filter(
        (row) =>
          `${new Date(row.at).getFullYear()}-${String(
            new Date(row.at).getMonth() + 1,
          ).padStart(2, "0")}` === openMonth,
      )
    : commissions;

  return (
    <AgentPageState
      loading={loading}
      error={error}
      unavailable={unavailable}
      reload={reload}
    >
      <div style={styles.page}>
        <h1 style={styles.h1}>Earnings</h1>
        <p style={{ ...styles.muted, marginTop: 0 }}>
          Commission is a share of what was collected after card fees — never of
          list price.
        </p>

        <div style={styles.statGrid}>
          <Stat label="Earned to date" value={money(data?.totals?.earnedCents)} />
          <Stat
            label="Accrued"
            value={money(data?.totals?.pendingCents)}
            hint="Earned, not yet in a payout"
          />
          <Stat
            label="Moved to payouts"
            value={money(data?.totals?.paidToLedgerCents)}
            hint="Written to the payout ledger"
          />
          <Stat
            label="Reversed"
            value={money(data?.totals?.reversedCents)}
            hint="Refunds and chargebacks"
          />
        </div>

        <div style={styles.card}>
          <h2 style={styles.h2}>By month</h2>
          {months.length === 0 ? (
            <div style={styles.empty}>No earnings yet.</div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Month</th>
                  <th style={styles.th}>Collected (net)</th>
                  <th style={styles.th}>Payments</th>
                  <th style={styles.th}>Your commission</th>
                </tr>
              </thead>
              <tbody>
                {months.map((row) => (
                  <tr
                    key={row.label}
                    onClick={() =>
                      setOpenMonth(openMonth === row.label ? null : row.label)
                    }
                    style={{ cursor: "pointer" }}
                  >
                    <td style={styles.td}>
                      <strong>{formatMonth(row.label)}</strong>
                      {openMonth === row.label ? (
                        <span style={styles.muted}> · showing only this month</span>
                      ) : null}
                    </td>
                    <td style={styles.td}>{money(row.netCollectedCents)}</td>
                    <td style={styles.td}>{row.count}</td>
                    <td style={styles.td}>
                      <strong>{money(row.commissionCents)}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div style={styles.card}>
          <h2 style={styles.h2}>
            Commission detail{openMonth ? ` · ${formatMonth(openMonth)}` : ""}
          </h2>
          {visible.length === 0 ? (
            <div style={styles.empty}>Nothing in this period.</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Date</th>
                    <th style={styles.th}>Business</th>
                    <th style={styles.th}>Collected</th>
                    <th style={styles.th}>Card fee</th>
                    <th style={styles.th}>Base</th>
                    <th style={styles.th}>Rate</th>
                    <th style={styles.th}>Commission</th>
                    <th style={styles.th}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((row) => (
                    <tr key={row.id}>
                      <td style={styles.td}>{formatDate(row.at)}</td>
                      <td style={styles.td}>{row.businessName}</td>
                      <td style={styles.td}>{money(row.grossCollectedCents)}</td>
                      <td style={styles.td}>−{money(row.stripeFeeCents)}</td>
                      <td style={styles.td}>{money(row.netCollectedCents)}</td>
                      <td style={styles.td}>{row.commissionPercent}%</td>
                      <td style={styles.td}>
                        <strong>{money(row.commissionCents)}</strong>
                      </td>
                      <td style={styles.td}>
                        <Badge tone={payoutTone(row.status)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AgentPageState>
  );
}
